/* eslint-disable react/prop-types */
import { Alert, Col, Divider, Row, Space } from 'antd';
import { useFormikContext } from 'formik';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { type EventApplicationModuleSpecsFragment } from '../../../../../api/fragments/EventApplicationModuleSpecs';
import {
    CreateEventApplicationModuleDocument,
    type CreateEventApplicationModuleMutation,
    type CreateEventApplicationModuleMutationVariables,
} from '../../../../../api/mutations/createEventApplicationModule';
import {
    ApplicationMarket,
    EmailContentUpdateType,
    type EventApplicationModuleInitialSettings,
} from '../../../../../api/types';
import CascaderField from '../../../../../components/fields/CascaderField';
import InputField from '../../../../../components/fields/InputField';
import SelectField from '../../../../../components/fields/SelectField';
import SwitchField from '../../../../../components/fields/SwitchField';
import { hasValue } from '../../../../../utilities/fp';
import useSystemSwitchData from '../../../../../utilities/useSystemSwitchData';
import validators from '../../../../../utilities/validators';
import EventApplicationModulePage, { type EventApplicationModulePageProps } from '../../EventApplicationModulePage';
import { newZealandMarketValidator, singaporeMarketValidator } from '../../shared/validators';
import { CounterHelp, CounterSettingGroup, createCounterSettingsValidator } from './shared';
import useModuleOptions from './shared/useModuleOptions';
import { type ModuleImplementation } from './types';

type EventApplicationModuleImplementation = ModuleImplementation<
    EventApplicationModuleSpecsFragment,
    EventApplicationModuleInitialSettings
>;

const validator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredString('agreementsModuleId'),
    validators.requiredString('customerModuleId'),
    validators.requiredString('vehicleModuleId'),
    createCounterSettingsValidator('eventCounter'),
    createCounterSettingsValidator('applicationCounter'),
    createCounterSettingsValidator('leadCounter'),
    createCounterSettingsValidator('reservationCounter'),
    createCounterSettingsValidator('appointmentCounter'),

    validators.only(values => hasValue('bankModuleId')(values), validators.requiredString('market')),
    validators.requiredBoolean('isOcrEnabled'),
    validators.requiredBoolean('showResetKYCButton'),
    validators.only(values => {
        const marketType = get('market', values);

        return marketType === ApplicationMarket.Singapore;
    }, singaporeMarketValidator),
    validators.only(values => {
        const marketType = get('market', values);

        return marketType === ApplicationMarket.NewZealand;
    }, newZealandMarketValidator)
);

const Form: EventApplicationModuleImplementation['FormComponent'] = ({ company }) => {
    const { t } = useTranslation(['moduleDetails']);

    const { values } = useFormikContext<EventApplicationModuleInitialSettings>();

    const moduleOptions = useModuleOptions(company, { addNoneOptions: { appointments: true } });

    const { yesNoSwitch } = useSystemSwitchData();

    return (
        <>
            <Row gutter={10}>
                <Col md={8} xs={24}>
                    <InputField
                        {...t('moduleDetails:fields.displayName', { returnObjects: true })}
                        name="displayName"
                        required
                    />
                </Col>
                <Col md={8} xs={24}>
                    <SwitchField
                        {...yesNoSwitch}
                        {...t('moduleDetails:fields.isOcrEnabled', { returnObjects: true })}
                        name="isOcrEnabled"
                    />
                </Col>
            </Row>
            <Divider>Dependencies</Divider>
            <Space direction="vertical" style={{ width: '100%' }}>
                <Alert
                    description={t('moduleDetails:alerts.moduleImmutableDependencies.content')}
                    message={t('moduleDetails:alerts.moduleImmutableDependencies.title')}
                    type="warning"
                    showIcon
                />
                <Row gutter={10}>
                    <Col md={12} xs={24}>
                        <SelectField
                            {...t('moduleDetails:fields.agreementsModuleId', { returnObjects: true })}
                            name="agreementsModuleId"
                            options={moduleOptions.agreements}
                            required
                            showSearch
                        />
                    </Col>

                    <Col md={12} xs={24}>
                        <SelectField
                            {...t('moduleDetails:fields.customerModuleId', { returnObjects: true })}
                            name="customerModuleId"
                            options={moduleOptions.customers}
                            required
                            showSearch
                        />
                    </Col>
                    <Col md={12} xs={24}>
                        <SelectField
                            {...t('moduleDetails:fields.vehicleModuleId', { returnObjects: true })}
                            name="vehicleModuleId"
                            options={moduleOptions.vehicles}
                            required
                            showSearch
                        />
                    </Col>

                    <Col md={12} xs={24}>
                        <CascaderField
                            {...t('moduleDetails:fields.liveChatSetting', { returnObjects: true })}
                            name="liveChatSettingId"
                            options={moduleOptions.liveChat}
                            showSearch
                        />
                    </Col>

                    <Col md={12} xs={24}>
                        <SelectField
                            {...t('moduleDetails:fields.appointmentModuleId', { returnObjects: true })}
                            name="appointmentModuleId"
                            options={moduleOptions.appointments}
                            showSearch
                        />
                    </Col>

                    {values.appointmentModuleId && values.appointmentModuleId !== null && (
                        <Col md={12} xs={24}>
                            <SwitchField
                                {...yesNoSwitch}
                                {...t('moduleDetails:fields.displayAppointmentDatepicker', { returnObjects: true })}
                                name="displayAppointmentDatepicker"
                            />
                        </Col>
                    )}

                    <Col md={12} xs={24}>
                        <SwitchField
                            {...yesNoSwitch}
                            {...t('moduleDetails:fields.showResetKYCButton', { returnObjects: true })}
                            name="showResetKYCButton"
                        />
                    </Col>
                </Row>
            </Space>
            <Divider>ID Generators</Divider>
            <Space direction="vertical" style={{ width: '100%' }}>
                <CounterHelp />
                <Row gutter={10}>
                    <CounterSettingGroup name="eventCounter" />
                    <CounterSettingGroup name="applicationCounter" />
                    <CounterSettingGroup name="leadCounter" />
                    <CounterSettingGroup name="reservationCounter" />
                    <CounterSettingGroup name="appointmentCounter" />
                </Row>
            </Space>
        </>
    );
};

const implementation: EventApplicationModuleImplementation = {
    PageComponent: ({ module }: EventApplicationModulePageProps) => <EventApplicationModulePage module={module} />,
    getTitle: t => t('moduleDetails:implementations.EventApplicationModule.className'),
    getValidator: () => validator,
    FormComponent: Form,
    create: async (apolloClient, companyId, values) =>
        apolloClient
            .mutate<CreateEventApplicationModuleMutation, CreateEventApplicationModuleMutationVariables>({
                mutation: CreateEventApplicationModuleDocument,
                variables: { companyId, settings: values },
            })
            .then(({ data }) => data.module),
    initialValues: {
        isOcrEnabled: false,
        emailContents: {
            emailContentUpdateType: EmailContentUpdateType.Module,

            submitOrder: {
                introTitle: {
                    defaultValue: {
                        defaultValue: '',
                        overrides: [],
                    },
                    overrides: [],
                },
                contentText: {
                    defaultValue: {
                        defaultValue: '',
                        overrides: [],
                    },
                    overrides: [],
                },
                subject: {
                    defaultValue: {
                        defaultValue: '',
                        overrides: [],
                    },
                    overrides: [],
                },
            },
        },
        showResetKYCButton: false,
        showRemoteFlowButtonInKYCPage: false,
        displayAppointmentDatepicker: false,
    },
};

export default implementation;
